// Actions
import { ClearAccessTokenAction, FetchAccessTokenAction, FetchAccessTokenFailedAction, FetchAccessTokenSucceededAction } from "@ms/planner-actions/lib/local/auth/AccessTokenActions";
// Utilities
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { isTokenExpiryWithinBufferPeriod } from "@ms/uno-utilities/lib/local/AuthUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { compositeKeyFromStringArray } from "@ms/uno-utilities/lib/local/StringUtilities";
/**
 * Module for Auth operations
 */ export class AuthModule {
    get name() {
        return "auth";
    }
    /**
     * Generates a cache key for deduplicating in-flight token requests
     * @param scopes The scopes array
     * @param claims Optional claims parameter
     */ getTokenCacheKey(scopes, claims) {
        const scopeKey = compositeKeyFromStringArray(scopes);
        return claims ? `${scopeKey}:${claims}` : scopeKey;
    }
    async fetchAccessTokenAsync(resource, claims, silent) {
        this.loggers.traceLogger.logTrace(0x1e198350 /* tag_4gynq */ , TraceLevel.Verbose, `Fetch Access Token [Resource=${resource}]`);
        return this.fetchAccessTokenAsyncFromStoreOrServer({
            resource,
            claims,
            silent
        });
    }
    async fetchAccessTokenForScopesAsync(scopes, claims, silent) {
        this.loggers.traceLogger.logTrace(0x1e057307 /* tag_4bxmh */ , TraceLevel.Verbose, `Fetch Access Token [Scopes=${scopes}]`);
        return this.fetchAccessTokenAsyncFromStoreOrServer({
            scopes,
            claims,
            silent
        });
    }
    async fetchAccessTokenAsyncFromStoreOrServer(params) {
        const scopes = "scopes" in params ? params.scopes : [
            `${params.resource}/.default`
        ];
        const tokenInfo = this.authStore.getAccessToken(scopes, params.claims);
        if (tokenInfo.data != null) {
            const config = this.configProvider();
            const expiryBuffer = config.settings.auth.authTokenExpiryBufferSeconds;
            const validationResult = !isTokenExpiryWithinBufferPeriod(tokenInfo.data.expiry, expiryBuffer);
            // If the token is not expired or within expiry buffer period, return it
            if (validationResult) {
                this.loggers.traceLogger.logTrace(0x1e19834f /* tag_4gynp */ , TraceLevel.Verbose, `Fetch Access Token Success [Scopes=${scopes}][Source=Store][Expiry=${tokenInfo.data.expiry?.toISOString()}][Buffer=${expiryBuffer}][Valid=${validationResult}]`);
                return Promise.resolve(tokenInfo.data);
            }
            this.loggers.traceLogger.logTrace(0x1e19834e /* tag_4gyno */ , TraceLevel.Info, `Fetch Access Token Validation Failure [Scopes=${scopes}][Source=Store][Expiry=${tokenInfo.data.expiry?.toISOString()}][Buffer=${expiryBuffer}][Valid=${validationResult}]`);
        }
        // Check for existing in-flight request and return it to deduplicate concurrent calls
        const cacheKey = this.getTokenCacheKey(scopes, params.claims);
        const pendingRequest = this.pendingTokenRequests.get(cacheKey);
        if (pendingRequest) {
            this.loggers.traceLogger.logTrace(0x1e0844d4 /* tag_4cetu */ , TraceLevel.Verbose, `Fetch Access Token - Returning existing in-flight request [Scopes=${scopes}]`);
            return pendingRequest;
        }
        // Create new request and cache the promise to deduplicate concurrent requests
        const tokenPromise = this.fetchAccessTokenInternal(params);
        this.pendingTokenRequests.set(cacheKey, tokenPromise);
        // Clear from cache when complete (success or failure)
        // Attach no-op catch to prevent unhandled rejection warning on the cached promise
        // Actual errors are still propagated to callers via their awaits
        tokenPromise.catch(()=>{
        /* no-op: errors are handled by callers */ }).finally(()=>{
            this.pendingTokenRequests.delete(cacheKey);
        });
        return tokenPromise;
    }
    async refreshAccessTokenAsync(resource, claims, silent) {
        this.loggers.traceLogger.logTrace(0x1e19834d /* tag_4gynn */ , TraceLevel.Verbose, `Refresh Access Token Started for [Resource=${resource}]`);
        const action = new ClearAccessTokenAction([
            `${resource}/.default`
        ]);
        await this.dispatcher.dispatchActionAsync(action);
        return this.fetchAccessTokenInternal({
            resource,
            claims,
            silent
        });
    }
    async fetchAccessTokenInternal(params) {
        const { claims, silent } = params;
        const actionId = LocalIdGenerator.generate();
        const scopesArray = "scopes" in params ? params.scopes : [
            `${params.resource}/.default`
        ];
        const action = new FetchAccessTokenAction(actionId, scopesArray, claims);
        await this.dispatcher.dispatchActionAsync(action);
        this.loggers.traceLogger.logTrace(0x1e19834c /* tag_4gynm */ , TraceLevel.Verbose, `Fetch Access Token started for [Scopes=${scopesArray}][Silent=${silent}]`);
        try {
            const accessTokenWithExpiry = await this.authService.fetchAccessTokenAsync(params);
            this.loggers.traceLogger.logTrace(0x1e19834b /* tag_4gynl */ , TraceLevel.Verbose, `Fetch Access Token Success [Scopes=${scopesArray}][Source=AuthService]`);
            const successAction = new FetchAccessTokenSucceededAction(actionId, scopesArray, accessTokenWithExpiry.accessToken, accessTokenWithExpiry.expiry, claims);
            await this.dispatcher.dispatchActionAsync(successAction);
            return Promise.resolve(accessTokenWithExpiry);
        } catch (error) {
            const failedRequestResult = error;
            this.loggers.traceLogger.logTrace(0x1e19834a /* tag_4gynk */ , TraceLevel.Error, `Fetch Access token Failure [Scopes=${scopesArray}][Source=AuthService][Subsource=${failedRequestResult.response == null ? "Host" : "OBOService"}]`);
            const failedAction = new FetchAccessTokenFailedAction(actionId, scopesArray, claims, failedRequestResult.error ?? undefined);
            await this.dispatcher.dispatchActionAsync(failedAction);
            return Promise.reject(failedRequestResult);
        }
    }
    /**
     * Initializes an instance of an entity module
     * @param dispatcher The dispatcher the SPA uses to dispatch actions
     * @param loggers Loggers for telemetry
     * @param authService Auth service instance
     * @param authStore Auth store instance
     * @param configProvider Delegate to retrieve the app configuration
     */ constructor(dispatcher, loggers, authService, authStore, configProvider){
        this.dispatcher = dispatcher;
        this.loggers = loggers;
        this.authService = authService;
        this.authStore = authStore;
        this.configProvider = configProvider;
        /** Track in-flight token requests to deduplicate concurrent calls for the same resource */ this.pendingTokenRequests = new Map();
    }
}
